import React, { FC, useContext, useMemo, useRef } from 'react';
import { DetailedInfo, DetailedInfoContainer, DetailsDescription, Grid, Title } from '@investoro/core';
import styled from 'styled-components';
import ChatbotContext from 'utils/context/ChatbotContext';
import { formatDescription } from 'utils/formatters/formatDescription';
import { useWindowWidth } from 'utils/useWindowWidth';

import { BoxContainer } from 'components/common/BoxContainer';
import DetailValueElement from 'components/common/Card/common/DetailValueElement/DetailValueElement';
import MetaValueElement, { MetaValueElementProperties } from 'components/common/Card/common/MetaValueElement/MetaValueElement';
import { ArchivedLabel, LabelsContainer, OfferLabel, StatusLabel } from 'components/common/Card/common/OfferLabels';
import OrganizationContainer, { OrganizationProps } from 'components/common/Card/common/V2CardBodyDefault/OrganizationContainer';
import PredicetdPriceAndCalculationContainer from 'components/common/Card/common/V2CardBodyDefault/PredictedPriceAndCalculationContainer';
import Icon, { IconEnum } from 'components/common/Icon';
import LendiButton from 'components/common/Lendi/LendiButton';
import RatingBar from 'components/common/RatingBar/RatingBar';
import { TabType } from 'components/common/Tabs/V2Tabs';
import TitleWithSecondValueAndTooltip from 'components/common/TitleWithSecondValueAndTooltip';
import { Variant } from 'components/functions/offerListFunctions/saleOfferListFunctions/saleOfferListFunctions';

const SwipeRight = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: 0;
    right: 0;
    width: 50px;
    height: 100%;
    background: linear-gradient(270deg, var(--color-white) 65.18%, rgba(255, 255, 255, 0) 100%);
    border-radius: 10px;
    z-index: 1;

    span {
        height: 100%;
        width: 100%;
    }
`;

const SwipeLeft = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: 0;
    left: 0;
    width: 50px;
    height: 100%;
    background: linear-gradient(90deg, var(--color-white) 65.18%, rgba(255, 255, 255, 0) 100%);
    border-radius: 10px;
    z-index: 1;

    span {
        height: 100%;
        width: 100%;
    }
`;

const File = styled.button`
    background: var(--color-primary);
    color: var(--color-white);
    padding: 5px 10px;
    border-radius: 5px;
    border: none;
    cursor: pointer;
    transition: background 0.3s;

    &:hover {
        background: var(--color-primary-70);
    }
`;

const FilesWrapper = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
`;

const SecondBoxWrapper = styled.div`
    display: flex;
    gap: 20px;
    justify-content: space-between;

    @media (max-width: 786px) {
        flex-direction: column;
        align-items: flex-start;
        justify-content: center;
    }
`;

const ChatButton = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    background: var(--label-color-active);
    color: var(--color-primary);
    border: none;
    border-radius: var(--box-border-radius);
    padding: 8px 14px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: opacity 0.2s ease-in-out;

    &:hover {
        color: var(--color-white);
        background: var(--color-alt);

        img {
            filter: invert(100%) sepia(100%) grayscale(100%) brightness(200%);
        }
    }
`;

const LeftSection = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
`;

const RatingAndOrganizationContainer = styled.div`
    display: flex;
    gap: 20px;
    align-items: center;
`;

interface DetailsElements {
    icon: IconEnum;
    text: string;
    value?: string;
    isBlurred?: boolean;
    url?: string;
    predictedValue?: string;
    prefix?: string;
    suffix?: string;
    tooltipText?: string;
    variant?: Variant;
    minWidth?: number;
    elementKey?: string;
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
    onClick?: () => void;
    showTooltip?: boolean;
}

export interface V2DetailsTabsProps {
    description?: string;
    title?: string;
    address?: string;
    predictedAddress?: string;
    detailsElements: DetailsElements[];
    withPrediction?: boolean;
    predictedPrice?: string;
    calculationValue?: string;
    hasPredictionRole?: boolean;
    hasCalculationRole?: boolean;
    documents?: string[];
    meta?: MetaValueElementProperties[];
    handleDocumentClick?: (document: string) => void;
    organizationContainer?: OrganizationProps;
    score?: number;
    scorePrecision?: number;
    openOfferTab?: (tab: TabType) => void;
    showLendiButton?: boolean;
    isDuplicate?: boolean;
    lowestPriceLabel?: boolean;
    privateOfferLabel?: boolean;
    archivedLabel?: boolean;
    adContext?: { adId: number | string; adModule: string };
}

const V2DetailsTab: FC<V2DetailsTabsProps> = ({
    description,
    title,
    address,
    detailsElements,
    documents,
    handleDocumentClick,
    meta,
    organizationContainer,
    score,
    scorePrecision,
    predictedPrice,
    calculationValue,
    hasPredictionRole,
    hasCalculationRole,
    openOfferTab,
    predictedAddress,
    showLendiButton,
    isDuplicate,
    lowestPriceLabel,
    privateOfferLabel,
    archivedLabel,
    adContext
}) => {
    const { openWithAdContext } = useContext(ChatbotContext);
    const gridRef = useRef<HTMLDivElement>(null);
    const width = useWindowWidth();
    const memoizedDetailsElements = useMemo(() => detailsElements, [detailsElements]);

    const memoizedKeys = useMemo(() => {
        return meta && meta.length > 0
            ? <BoxContainer>
                <Title>Słowa kluczowe:</Title>
                {meta.map((metaItem) => {
                    return <MetaValueElement
                        key={metaItem.metaKey}
                        {...metaItem}
                    />;
                })}
            </BoxContainer>
            : null;
    }, [meta]);

    const swipeToRight = () => {
        const scrollPosition = gridRef.current!.scrollLeft;
        const scrollWidth = gridRef.current!.scrollWidth;
        const clientWidth = gridRef.current!.clientWidth;

        if (scrollPosition + clientWidth < scrollWidth && window.innerWidth < 991) {
            gridRef.current!.scrollLeft += 100;
        }
    };

    const swipeToLeft = () => {
        const scrollPosition = gridRef.current!.scrollLeft;
        const clientWidth = gridRef.current!.clientWidth;

        if (scrollPosition + clientWidth > 0 && window.innerWidth < 991) {
            gridRef.current!.scrollLeft -= 100;
        }
    };

    return (
        <>
            <BoxContainer>
                {(isDuplicate !== undefined || lowestPriceLabel || privateOfferLabel || archivedLabel) &&
                    <LabelsContainer>
                        {archivedLabel && <ArchivedLabel>Oferta nieaktualna</ArchivedLabel>}
                        {isDuplicate !== undefined && <StatusLabel isDuplicate={isDuplicate}>{isDuplicate ? 'Duplikat' : 'Oryginał'}</StatusLabel>}
                        {lowestPriceLabel && <OfferLabel>Duplikat z niższą ceną</OfferLabel>}
                        {privateOfferLabel && <OfferLabel>Duplikat od os. prywatnej</OfferLabel>}
                    </LabelsContainer>
                }
                <Title>{title ?? '-'}</Title>
                <TitleWithSecondValueAndTooltip title={address ?? '-'} additionalTitle={predictedAddress} hasPredictionRole={hasPredictionRole} isInModal/>
            </BoxContainer>
            {organizationContainer || score || scorePrecision || adContext
                ? <BoxContainer>
                    <SecondBoxWrapper>
                        <LeftSection>
                            {showLendiButton && <LendiButton />}
                            {adContext &&
                                <ChatButton onClick={() => openWithAdContext(adContext.adId, adContext.adModule)}>
                                    <Icon icon={IconEnum.MESSAGE} />
                                    Porozmawiaj o ogłoszeniu
                                </ChatButton>
                            }
                            {predictedPrice || calculationValue ? <PredicetdPriceAndCalculationContainer hasPredictionRole={hasPredictionRole} hasCalculationRole={hasCalculationRole} isInModal openOfferTab={openOfferTab} predictedPrice={predictedPrice} calculationValue={calculationValue}/> : null}
                        </LeftSection>
                        <RatingAndOrganizationContainer>
                            {organizationContainer && <OrganizationContainer organizationContainer={organizationContainer} />}
                            <RatingBar isCardBox score={score ? score : 0} scorePrecision={scorePrecision} />
                        </RatingAndOrganizationContainer>
                    </SecondBoxWrapper>
                </BoxContainer>
                : null}

            <BoxContainer>
                {width < 991 && <SwipeLeft onClick={swipeToLeft}>
                    <Icon icon={IconEnum.ANGLE_LEFT}/>
                </SwipeLeft>}
                <Grid ref={gridRef}>
                    {memoizedDetailsElements.map((element, index) => {
                        const handleClick = () => {
                            if (element.url) {
                                window.open(element.url, '_blank', 'noopener,noreferrer');
                            }
                        };

                        return (
                            <DetailedInfoContainer
                                key={index}
                                onClick={element.url ? handleClick : undefined}
                                isPointer={element.url ? true : false}>
                                <Icon icon={element.icon}/>
                                <DetailedInfo>
                                    <span dangerouslySetInnerHTML={{ __html: element.text }}/>
                                    <DetailValueElement
                                        isBlurred={element.isBlurred}
                                        variant={element.variant}
                                        minWidth={element.minWidth}
                                        value={element.value || ''}
                                        prefix={element.prefix}
                                        suffix={element.suffix}
                                        tooltipText={element.tooltipText}
                                        showTooltip={element.showTooltip}
                                        onMouseEnter={element.onMouseEnter}
                                        onMouseLeave={element.onMouseLeave}
                                        onClick={element.onClick}
                                    />
                                </DetailedInfo>
                            </DetailedInfoContainer>
                        );
                    })}

                </Grid>
                {width < 991 && <SwipeRight onClick={swipeToRight}>
                    <Icon icon={IconEnum.ANGLE_RIGHT}/>
                </SwipeRight>}
            </BoxContainer>
            <BoxContainer>
                <Title>Szczegóły ogłoszenia:</Title>
                {description &&
                    <DetailsDescription dangerouslySetInnerHTML={{ __html: formatDescription(description) }}/>}
            </BoxContainer>
            {memoizedKeys}
            {documents && documents.length > 0
                ? <BoxContainer>
                    <Title>Dokumenty:</Title>
                    <FilesWrapper>
                        {documents.map((document, index) => {
                            return (
                                <DetailsDescription key={index}>
                                    <File onClick={() => handleDocumentClick?.(document)}>{document}</File>
                                </DetailsDescription>
                            );
                        })}
                    </FilesWrapper>
                </BoxContainer>
                : null}
        </>
    );
};

export default V2DetailsTab;
